import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FUEL_TYPE_LABELS, carLabel, clientDisplayName } from '@garage/shared'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { Spinner } from '@/ui'

/**
 * Keeps the first 4 and last 2 characters, masks the rest. Mechanics need
 * enough of the number to confirm they have the right customer, not enough
 * to call or text them directly.
 */
function censorPhone(phone: string): string {
  if (phone.length <= 6) return phone
  return `${phone.slice(0, 4)}••••${phone.slice(-2)}`
}

/**
 * Printable job card: a plain HTML/CSS page (no Tailwind, no app chrome) so
 * anyone can open this file and tweak fonts, spacing or the logo size
 * without touching React or the design system.
 *
 * Opened in its own tab from the jobcard editor. Print with Ctrl/Cmd+P;
 * the .no-print button and page-frame around it disappear in @media print.
 */
export function JobCardPrintView() {
  const { id } = useParams<{ id: string }>()
  const pageRef = useRef<HTMLDivElement>(null)
  const [downloading, setDownloading] = useState(false)
  const { t, i18n } = useTranslation()

  const { data: jobcard, isLoading } = useQuery({
    queryKey: qk.jobcard(id ?? ''),
    queryFn: () => api.jobcards.get(id ?? ''),
    enabled: !!id,
  })

  const { data: settings } = useQuery({
    queryKey: qk.settings,
    queryFn: () => api.settings.get(),
  })

  if (isLoading || !jobcard) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size="md" />
      </div>
    )
  }

  const car = jobcard.carSnapshot
  // The complaint box is free text; each non-empty line becomes one bullet.
  const complaintLines = (jobcard.complaint ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  const handleDownloadPdf = async () => {
    if (!pageRef.current) return
    setDownloading(true)
    try {
      const html2pdf = (await import('html2pdf.js')).default
      await html2pdf()
        .set({
          filename: `job-card-${jobcard.number}.pdf`,
          margin: 0,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(pageRef.current)
        .save()
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <style>{PRINT_CSS}</style>

      <div className="no-print toolbar">
        <button className="toolbar-button" onClick={() => window.print()}>
          {t('common.print')}
        </button>
        <button className="toolbar-button" onClick={handleDownloadPdf} disabled={downloading}>
          {downloading ? t('jobCardPrint.preparingPdf') : t('jobCardPrint.downloadPdf')}
        </button>
      </div>

      <div className="sheet">
      <div className="page" ref={pageRef}>
        <header className="header">
          {settings && (
            <img
              className="logo"
              src={api.settings.logoUrl()}
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          )}
          <div className="header-text">
            <div className="business-name">{settings?.businessName || 'Cypek Garage'}</div>
            <h1 className="title">{t('jobCardPrint.jobCard')}</h1>
          </div>
          <div className="header-meta">
            <div>
              <span className="label">{t('jobCardPrint.no')}</span> {jobcard.number}
            </div>
            <div>
              <span className="label">{t('calendar.date')}</span> {new Date(jobcard.createdAt).toLocaleDateString(i18n.language === 'ar' ? 'ar-u-nu-latn' : undefined)}
            </div>
          </div>
        </header>

        <section className="grid">
          <div className="box">
            <h2>{t('jobcardEditor.customer')}</h2>
            <div className="row">
              <span className="label">{t('jobCardPrint.name')}</span>
              <span>{jobcard.client ? clientDisplayName(jobcard.client) : '—'}</span>
            </div>
            <div className="row">
              <span className="label">{t('clients.columns.phone')}</span>
              <span>{jobcard.client?.phone ? censorPhone(jobcard.client.phone) : '—'}</span>
            </div>
          </div>

          <div className="box">
            <h2>{t('carSelector.vehicleAriaLabel')}</h2>
            <div className="row">
              <span className="label">{t('jobCardPrint.car')}</span>
              <span>{carLabel(car)}</span>
            </div>
            <div className="row">
              <span className="label">{t('carForm.vin')}</span>
              <span>{car.vin || '—'}</span>
            </div>
            <div className="row">
              <span className="label">{t('carForm.colour')}</span>
              <span>{car.color || '—'}</span>
            </div>
            <div className="row">
              <span className="label">{t('carSelector.engine')}</span>
              <span>{car.engineSize || '—'}</span>
            </div>
            <div className="row">
              <span className="label">{t('carSelector.fuel')}</span>
              <span>{car.fuelType ? t(`carForm.fuelTypes.${car.fuelType}`, FUEL_TYPE_LABELS[car.fuelType]) : '—'}</span>
            </div>
            <div className="row">
              <span className="label">{t('jobCardPrint.odometer')}</span>
              <span>{jobcard.odometerKm != null ? `${jobcard.odometerKm} km` : '—'}</span>
            </div>
          </div>
        </section>

        <section className="box">
          <h2>{t('jobCardPrint.pointsToBeAddressed')}</h2>
          {complaintLines.length > 0 ? (
            <ul className="bullets">
              {complaintLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t('jobCardPrint.noComplaintRecorded')}</p>
          )}
        </section>
      </div>
      </div>
    </>
  )
}

/**
 * Plain CSS, deliberately not Tailwind: this is the file to open when the
 * printed layout needs to change. Edit colours, spacing or the page size
 * here directly.
 */
const PRINT_CSS = `
  @page {
    size: A4;
    margin: 16mm;
  }

  /* Always white paper, never the app's dark theme. */
  .sheet {
    color-scheme: light;
    background: #fff;
    min-height: 100vh;
  }

  .page {
    max-width: 190mm;
    margin: 0 auto;
    padding: 16px;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
  }

  .toolbar {
    position: fixed;
    top: 16px;
    right: 16px;
    display: flex;
    gap: 8px;
  }
  .toolbar-button {
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    background: #111;
    border: none;
    border-radius: 6px;
    cursor: pointer;
  }
  .toolbar-button:hover { background: #333; }
  .toolbar-button:disabled { background: #888; cursor: wait; }

  .header {
    display: flex;
    align-items: center;
    gap: 16px;
    border-bottom: 3px solid #111;
    padding-bottom: 12px;
    margin-bottom: 16px;
  }
  .logo { height: 56px; width: auto; object-fit: contain; }
  .header-text { flex: 1; }
  .business-name { font-size: 13px; color: #555; }
  .title {
    margin: 2px 0 0;
    font-size: 26px;
    letter-spacing: 2px;
    text-transform: uppercase;
  }
  .header-meta { text-align: right; font-size: 13px; line-height: 1.6; }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  .box {
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 16px;
  }
  .box h2 {
    margin: 0 0 8px;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #555;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    border-bottom: 1px solid #eee;
    font-size: 14px;
  }
  .row:last-child { border-bottom: none; }

  .label {
    font-weight: 600;
    color: #555;
    min-width: 80px;
  }

  .bullets {
    margin: 0;
    padding-left: 20px;
    font-size: 14px;
    line-height: 1.7;
  }
  .muted { color: #888; font-style: italic; margin: 0; }

  @media print {
    .no-print { display: none !important; }
    .page { padding: 0; max-width: none; }
    .box { break-inside: avoid; }
  }
`
